import {Component, EventEmitter, Input, OnDestroy, Output, TemplateRef} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';
import { LabOrderBusinessService } from '../../shared/lab-order-business.service';
import { LabOrder } from '../../shared/data/lab-order';
import { SubscriptionsContainer } from '../../shared/subscriptions-container';
import { DataConstants} from '../../shared/data/data-constants';
import { HttpErrorResponse } from '@angular/common/http';
import {UtilsService} from '../../shared/utils.service';
import { Messages } from '../../shared/messages';
import { ClearMessagesService } from '../../shared/clear-messages.service';

@Component({
  selector: 'modal-order-button',
  templateUrl: './modal.html',
  styleUrls: ['./modal.css']
})
export class ModalOrderButtonComponent implements OnDestroy {
  @Input() action: string;
  @Input() buttonTitle: string;
  @Input() tooltipText: string;
  @Input() labOrdNum: string;
  @Input() disableButton: boolean;
  @Output() eventEmitter = new EventEmitter<object>();
  modalTitle: string;
  modalHtml: SafeHtml;
  promptInvoiceNum = true;
  submitButtonText: string;
  errorMessage: string = null;
  labInvoiceNumber: any;
  modalRef: BsModalRef;
  labOrderData: LabOrder;
  subs = new SubscriptionsContainer();
  clickCnt = 0;

  constructor(
    private modalService: BsModalService,
    private sanitizer: DomSanitizer,
    private labOrderBusinessService: LabOrderBusinessService,
    private clearMessagesService: ClearMessagesService
  ) {}

  ngOnDestroy(): void {
    console.log(' Modal Component - ngOnDestroy ' );
    this.subs.dispose();
  }
  openModal(template: TemplateRef<any>) {
    console.log('Opening modal for: ', this.action, 'labOrderNumber: ', this.labOrdNum);

    // Setting flag to clear old status message in order component
    this.clearMessagesService.setClearMessages(true);

    this.clickCnt = this.clickCnt + 1;
    if (this.clickCnt > 1) {
      // Prevent 2nd click from opening Modal
      return;
    }
    this.initVars(this.action);

    // get the lab order and show modal
    this.subs.add = this.labOrderBusinessService.viewLabOrder(this.labOrdNum)
      .subscribe(
        (labOrder) => {
          console.log('labOrderNumber: ', this.labOrdNum, 'status: ', labOrder.status, 'trying to ', this.action);
          this.labOrderData = labOrder;
          this.labInvoiceNumber = labOrder.labInvoiceNumber;
          // Hide modal window if user try to modify legacy VSP orders
          if (this.displayModal()) {
            this.modalRef = this.modalService.show(template);
          }
          this.clickCnt = 0;
        },
        (error) => {
          this.processErrors(error, this.labOrdNum, this.action);
          // reset click count for errors too.
          this.clickCnt = 0;
        }
      );
  }

  // Hide modal window if user try to modify legacy VSP orders
  displayModal() {
    let display = true;
    if (this.action === 'MODIFY') {
      if (!UtilsService.isPrivateOrder(this.labOrderData) && !UtilsService.isAPIOrder(this.labOrderData)) {

        // inform user that order is not available for update
        alert(Messages.LEGACY_VSP_ORDER_NOT_UPDATABLE_MESSAGE);
        display = false;
      }
    }
    return display;
  }

  // send specific error details to console, but show
  // user-friendly message to user.
  processErrors(httpError: HttpErrorResponse, labOrderNumber: string, action: string): void {

    let consoleMessage: string = 'Error accessing order \'' + labOrderNumber + '\' - Action=' + action;
    let userMessage: string = 'Error accessing order \'' + labOrderNumber + '\'';

    // display error to console if anything returned
    if (httpError) {
      consoleMessage += ' httpCode=' + httpError.status + ' httpStatus=' + httpError.statusText;

      // order not found
      if (httpError.status === 404) {
        userMessage = 'Cannot find order \'' + labOrderNumber + '\'';
      }
    }
    alert(userMessage);
    console.error(consoleMessage);
  }

  closeModal() {
    this.clickCnt = 0;
    this.modalRef.hide();
  }

  hasError(): boolean {
    if (!this.labOrdNum) {
      this.errorMessage = 'Please go back and enter an order number.';
      return true;
    }

    this.errorMessage = this.checkActionAgainstStatus(this.labOrderData.status, this.action);
    return this.errorMessage ? true : false;
  }

  submitModal(action: string) {
    console.log('submitModal: action= ', action, 'labInvoiceNumber= ', this.labInvoiceNumber);
    // if invoice number is specified, let's make sure it's not more than 11 chars.
    if (this.labInvoiceNumber && this.labInvoiceNumber.length > DataConstants.INVOICE_NUMBER_LENGHT) {
      alert('Invoice number should be ' + DataConstants.INVOICE_NUMBER_LENGHT + ' characters or less.');
      return;
    }
    const labOrdNum: string = this.labOrdNum;
    const invoiceNumber: string = this.labInvoiceNumber;
    this.eventEmitter.emit({action, labOrdNum, invoiceNumber});
    this.closeModal();
  }

  /**
   *  Check if an action can be performed on an order based on status.
   *
   *  status:   status of the order
   *  action:   specified by the button clicked
   *
   */
  checkActionAgainstStatus (status: string, action: string): string {
    let checkErrorMessage: string = null;

    switch (action) {
      // can ACCEPT if order is in "MODIFIED_23" or "SUBMIT_05"
      case 'ACCEPT':
        if (status !== 'MODIFIED_23' && status !== 'SUBMIT_05') {
          checkErrorMessage = 'Cannot \'Accept\' an order in this status.';
        }
        break;

      // can COMPLETE if order is in "LAB_ACCEPTED_21"
      case 'COMPLETE':
        if (status !== 'LAB_ACCEPTED_21') {
          checkErrorMessage = 'Cannot \'Complete\' an order in this status.';
        }
        break;

      // can ASSIGN on any status
      case 'ASSIGN':
        break;

      // can VOID if order is in "LAB_ACCEPTED_21"
      case 'VOID':
        if (status !== 'LAB_ACCEPTED_21') {
          checkErrorMessage = 'Cannot \'Void\' an order in this status.';
        }
        break;

      // can MODIFY if order is in "LAB_ACCEPTED_21"
      case 'MODIFY':
        if (status !== 'LAB_ACCEPTED_21' && status !== 'MODIFIED_23') {
          checkErrorMessage = 'Cannot \'Modify\' an order in this status.';
        }
        break;

      default:
        console.error('Unknown action:', this.action);
    }

    // console.log("checkErrorMessage:", checkErrorMessage);
    return checkErrorMessage;
  }

  private initVars(action) {
    this.modalTitle = this.tooltipText;
    this.submitButtonText = this.modalTitle;
    this.promptInvoiceNum = true;

    // setting this.modalHtml from htmlToTrust
    let htmlToTrust = '';
    switch (action) {
      case 'ACCEPT':
        htmlToTrust = 'Accept this order?<br>';
        break;

      case 'COMPLETE':
        htmlToTrust = 'Complete this order?<br>';
        break;

      case 'ASSIGN':
        htmlToTrust = 'Assign this order?<br>';
        break;

      case 'VOID':
        htmlToTrust = 'Void this order?<br>';
        break;

      case 'MODIFY':
        htmlToTrust = 'Modify this order?<br>';
        break;

      default:
        console.error('Unknown action:', action);
    }
    this.modalHtml = this.sanitizer.bypassSecurityTrustHtml(htmlToTrust);
  }

}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-prod-build/src/app/orders/modal/modal.component.ts